import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
interface HeroVisualProps {
  className?: string;
}
export function HeroVisual({
  className = ''
}: HeroVisualProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
      });
    } catch {
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    container.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 0, 9);
    const group = new THREE.Group();
    scene.add(group);
    const coreGeo = new THREE.IcosahedronGeometry(1.5, 0);
    const coreMat = new THREE.MeshStandardMaterial({
      color: 0xc9aa78,
      metalness: 0.55,
      roughness: 0.32,
      flatShading: true
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    group.add(core);
    const edgesGeo = new THREE.EdgesGeometry(coreGeo);
    const edgesMat = new THREE.LineBasicMaterial({
      color: 0xf3eadb,
      transparent: true,
      opacity: 0.55
    });
    core.add(new THREE.LineSegments(edgesGeo, edgesMat));
    const shellGeo = new THREE.IcosahedronGeometry(2.4, 1);
    const shellMat = new THREE.MeshBasicMaterial({
      color: 0x5c98a2,
      wireframe: true,
      transparent: true,
      opacity: 0.2
    });
    const shell = new THREE.Mesh(shellGeo, shellMat);
    group.add(shell);
    const ringGeo = new THREE.TorusGeometry(3.0, 0.008, 8, 180);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xd9c29a,
      transparent: true,
      opacity: 0.45
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = Math.PI * 0.42;
    group.add(ring);
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const key = new THREE.DirectionalLight(0xfff4e0, 2.2);
    key.position.set(-3, 4, 5);
    scene.add(key);
    const rim = new THREE.PointLight(0x3e8f9a, 18, 20);
    rim.position.set(3, -3, 2);
    scene.add(rim);
    const render = () => renderer.render(scene, camera);
    const resize = () => {
      const {
        clientWidth: w,
        clientHeight: h
      } = container;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      render();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    const pointer = {
      x: 0,
      y: 0
    };
    const onMove = (e: PointerEvent) => {
      pointer.x = e.clientX / window.innerWidth * 2 - 1;
      pointer.y = e.clientY / window.innerHeight * 2 - 1;
    };
    let frame = 0;
    if (reduceMotion) {
      core.rotation.set(0.4, 0.6, 0);
      render();
    } else {
      window.addEventListener('pointermove', onMove);
      const clock = new THREE.Clock();
      const tick = () => {
        const t = clock.getElapsedTime();
        core.rotation.y = t * 0.18;
        core.rotation.x = t * 0.08;
        shell.rotation.y = -t * 0.05;
        ring.rotation.z = t * 0.04;
        group.rotation.y += (pointer.x * 0.25 - group.rotation.y) * 0.04;
        group.rotation.x += (pointer.y * 0.15 - group.rotation.x) * 0.04;
        render();
        frame = requestAnimationFrame(tick);
      };
      tick();
    }
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onMove);
      observer.disconnect();
      [coreGeo, edgesGeo, shellGeo, ringGeo].forEach(g => g.dispose());
      [coreMat, edgesMat, shellMat, ringMat].forEach(m => m.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
    };
  }, []);
  return <div ref={containerRef} className={`relative ${className}`} aria-hidden="true" data-id="element-38" data-mp="3pwx0ec7a924i" />;
}